import { Controller, useFormContext } from 'react-hook-form';
import { Input } from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import type { ContractFormValues } from '../../contract-form';
import { DateInput } from '@/components/date-input';

interface VnnicAdminFieldsProps {
  /** Absolute path prefix: `products.{i}.service_domain_metadata`. */
  metaPath: string;
}

/**
 * Step 3 of the VNNIC registrar form: technical admin (quản trị kỹ thuật) information.
 * Writes into `service_domain_metadata.domain_extra_fields.admin*` fields.
 * Mirrors legacy AdminInformation component (name, ID number, address,
 * email, phone, gender, fax, birthdate, company, position).
 * Address is plain text (no PermanentAddress lookup widget in this workspace).
 */
export function VnnicAdminFields({ metaPath }: VnnicAdminFieldsProps) {
  const { control } = useFormContext<ContractFormValues>();
  const extraPath = `${metaPath}.domain_extra_fields`;

  const field = (key: string) => `${extraPath}.${key}` as never;

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--vn-space-3)' }}>
        <Controller
          control={control}
          name={field('adminName')}
          render={({ field: f }) => (
            <Field label="Họ và tên quản trị">
              {(p) => (
                <Input {...p} type="text" placeholder="Họ và tên"
                  value={(f.value as string) ?? ''} onChange={f.onChange} onBlur={f.onBlur} name={f.name} ref={f.ref} />
              )}
            </Field>
          )}
        />
        <Controller
          control={control}
          name={field('adminID_Number')}
          render={({ field: f }) => (
            <Field label="Số CMND / CCCD">
              {(p) => (
                <Input {...p} type="text" placeholder="Số CMND / CCCD"
                  value={(f.value as string) ?? ''} onChange={f.onChange} onBlur={f.onBlur} name={f.name} ref={f.ref} />
              )}
            </Field>
          )}
        />
      </div>

      {/* Province / address — plain text fallback */}
      <Controller
        control={control}
        name={field('adminProvince')}
        render={({ field: f }) => (
          <Field label="Tỉnh / Thành phố">
            {(p) => (
              <Input {...p} type="text" placeholder="Tỉnh / Thành phố"
                value={(f.value as string) ?? ''} onChange={f.onChange} onBlur={f.onBlur} name={f.name} ref={f.ref} />
            )}
          </Field>
        )}
      />
      <Controller
        control={control}
        name={field('adminAddress')}
        render={({ field: f }) => (
          <Field label="Địa chỉ">
            {(p) => (
              <Input {...p} type="text" placeholder="Số nhà, đường, phường/xã"
                value={(f.value as string) ?? ''} onChange={f.onChange} onBlur={f.onBlur} name={f.name} ref={f.ref} />
            )}
          </Field>
        )}
      />

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--vn-space-3)' }}>
        <Controller
          control={control}
          name={field('adminEmail')}
          render={({ field: f }) => (
            <Field label="Email">
              {(p) => (
                <Input {...p} type="email" placeholder="email@example.com"
                  value={(f.value as string) ?? ''} onChange={f.onChange} onBlur={f.onBlur} name={f.name} ref={f.ref} />
              )}
            </Field>
          )}
        />
        <Controller
          control={control}
          name={field('adminPhone')}
          render={({ field: f }) => (
            <Field label="Điện thoại">
              {(p) => (
                <Input {...p} type="text" placeholder="0901234567"
                  value={(f.value as string) ?? ''} onChange={f.onChange} onBlur={f.onBlur} name={f.name} ref={f.ref} />
              )}
            </Field>
          )}
        />
        <Controller
          control={control}
          name={field('adminGender')}
          render={({ field: f }) => (
            <Field label="Giới tính">
              {(p) => (
                <Input {...p} type="text" placeholder="Nam / Nữ"
                  value={(f.value as string) ?? ''} onChange={f.onChange} onBlur={f.onBlur} name={f.name} ref={f.ref} />
              )}
            </Field>
          )}
        />
        <Controller
          control={control}
          name={field('adminFax')}
          render={({ field: f }) => (
            <Field label="Fax">
              {(p) => (
                <Input {...p} type="text" placeholder="Số fax"
                  value={(f.value as string) ?? ''} onChange={f.onChange} onBlur={f.onBlur} name={f.name} ref={f.ref} />
              )}
            </Field>
          )}
        />
        <Controller
          control={control}
          name={field('adminBirthdate')}
          render={({ field: f }) => (
            <Field label="Ngày sinh">
              {(p) => (
                <DateInput {...p}
                  value={(f.value as string) ?? ''} onChange={f.onChange} onBlur={f.onBlur} name={f.name} ref={f.ref} />
              )}
            </Field>
          )}
        />
        <Controller
          control={control}
          name={field('adminCompany')}
          render={({ field: f }) => (
            <Field label="Công ty">
              {(p) => (
                <Input {...p} type="text" placeholder="Tên công ty"
                  value={(f.value as string) ?? ''} onChange={f.onChange} onBlur={f.onBlur} name={f.name} ref={f.ref} />
              )}
            </Field>
          )}
        />
        <Controller
          control={control}
          name={field('adminPosition')}
          render={({ field: f }) => (
            <Field label="Chức vụ">
              {(p) => (
                <Input {...p} type="text" placeholder="Chức vụ"
                  value={(f.value as string) ?? ''} onChange={f.onChange} onBlur={f.onBlur} name={f.name} ref={f.ref} />
              )}
            </Field>
          )}
        />
      </div>
    </div>
  );
}
